import * as React from "react"
import { FormEvent } from "react"
import { Head, Link, router, useForm, usePage } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { PageHeader } from "@/components/PageHeader"
import { Button } from "@/components/ui/button"
import { DataRow, DataTable } from "@/components/ui/data-table"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { FileInput } from "@/components/ui/file-input"
import { Input } from "@/components/ui/input"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import { Collapsible, CollapsibleGroup } from "@/components/ui/collapsible"
import {
  AuditStatusBadge,
  CopyLinkButton,
  KIND_LABELS,
  formatBytes,
  formatClock,
  formatDate,
  formatDateTime,
  formatDuration,
  type AuditDetail,
  type AuditSession,
  type AuditVersion,
} from "@/components/admin/audits"

import type { PageProps } from "@/types/inertia"

type Props = {
  audit: AuditDetail
  versions: AuditVersion[]
  sessions: AuditSession[]
}

export default function AdminAuditsShow({ audit, versions, sessions }: Props) {
  const { props } = usePage<PageProps>()
  const errors = props.errors ?? {}
  const title = audit.client_name
  const description = `Hosted audit for ${audit.client_name}: link, versions and lifecycle controls.`

  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Head>
      <AdminShell wide>
        <PageHeader
          title={title}
          description={audit.kind ? (KIND_LABELS[audit.kind] ?? audit.kind) : "Hosted audit"}
          actions={
            <Button asChild variant="ghost">
              <Link href="/admin/audits">All audits</Link>
            </Button>
          }
        />

        {props.flash?.notice && (
          <p className="mt-6 text-sm text-accent">{props.flash.notice}</p>
        )}

        <section className="mt-8">
          <DataTable>
            <DataRow title="Status">
              <AuditStatusBadge status={audit.status} />
            </DataRow>
            <DataRow title="Link">
              <div className="flex flex-wrap items-center gap-2">
                <a href={audit.url} target="_blank" rel="noopener noreferrer" className="break-all">
                  {audit.url}
                </a>
                <CopyLinkButton url={audit.url} />
              </div>
              <p className="mt-1 text-xs text-ink-muted">
                Opening this while signed in shows the deck with a “Preview — not
                tracked” banner, so your own views are never recorded.
              </p>
            </DataRow>
            <DataRow title="Expires">{formatDate(audit.expires_at)}</DataRow>
            <DataRow title="Published">{formatDate(audit.created_at)}</DataRow>
            <DataRow title="First opened">
              {audit.first_opened_at ? (
                formatDate(audit.first_opened_at)
              ) : (
                <span className="text-ink-muted">Not yet</span>
              )}
            </DataRow>
          </DataTable>
        </section>

        <SessionsSection sessions={sessions} />

        <section className="mt-10">
          <h2>Versions</h2>
          <p className="mt-1 text-sm text-ink-muted">
            A rebuild becomes a new version at the same URL, so a link already
            sent keeps working.
          </p>

          <div className="mt-4 overflow-hidden rounded-lg border border-hairline bg-page">
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Version</TableHeaderCell>
                  <TableHeaderCell className="text-right">Slides</TableHeaderCell>
                  <TableHeaderCell className="text-right">Size</TableHeaderCell>
                  <TableHeaderCell>Uploaded</TableHeaderCell>
                  <TableHeaderCell>Opens with</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {versions.map((version) => (
                  <TableRow key={version.id}>
                    <TableCell className="font-medium text-ink-display tabular-nums">
                      v{version.number}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {version.slide_count}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatBytes(version.byte_size)}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-ink-muted">
                      {formatDate(version.created_at)}
                    </TableCell>
                    <TableCell className="text-ink-muted">
                      {version.slide_titles[0] ?? "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </section>

        <section className="mt-10 grid gap-8 lg:grid-cols-2">
          <UploadVersionForm auditId={audit.id} error={errors.file} />
          <div className="space-y-8">
            <ExpiryForm
              auditId={audit.id}
              expiresOn={audit.expires_on}
              error={errors.expires_at}
            />
            <LifecycleActions audit={audit} />
          </div>
        </section>
      </AdminShell>
    </>
  )
}

// The reason the hosted URL exists: not "was it opened" but which slides held
// someone, and what they went back to.
function SessionsSection({ sessions }: { sessions: AuditSession[] }) {
  return (
    <section className="mt-10">
      <h2>Views</h2>
      <p className="mt-1 text-sm text-ink-muted">
        One entry per visit, newest first, in the order the slides were actually
        read. A slide read twice appears twice.
      </p>

      {sessions.length === 0 ? (
        <p className="mt-4 rounded-lg border border-hairline bg-page px-4 py-6 text-sm text-ink-muted">
          No opens yet. Email scanners don’t count — a view is only recorded
          after 5 seconds of real reading, so a link that has been delivered but
          not read shows nothing here.
        </p>
      ) : (
        <CollapsibleGroup className="mt-4">
          {sessions.map((session, index) => (
            <Collapsible
              key={session.id}
              defaultOpen={index === 0}
              summary={<SessionSummary session={session} />}
            >
              <SessionPath views={session.slide_views} />
            </Collapsible>
          ))}
        </CollapsibleGroup>
      )}
    </section>
  )
}

function SessionSummary({ session }: { session: AuditSession }) {
  const detail = [
    `${session.slide_views.length} slide${session.slide_views.length === 1 ? "" : "s"}`,
    session.device_type,
    session.version_number ? `v${session.version_number}` : null,
  ].filter(Boolean)

  return (
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
      <span className="text-sm text-ink-display">
        {formatDateTime(session.started_at)}
      </span>
      {session.returning ? (
        <Badge tone="accent" title="This device has opened the deck before">
          Viewer {session.viewer_label} · returning
        </Badge>
      ) : (
        <Badge tone="muted" title="First time this device opened the deck">
          Viewer {session.viewer_label}
        </Badge>
      )}
      <span className="text-xs tabular-nums text-ink-muted">
        {formatDuration(session.active_ms)}
      </span>
      <span className="text-xs text-ink-muted">{detail.join(" · ")}</span>
    </div>
  )
}

function SessionPath({ views }: { views: AuditSession["slide_views"] }) {
  if (views.length === 0) {
    return <p className="text-sm text-ink-muted">No slides recorded.</p>
  }

  const longest = Math.max(...views.map((view) => view.dwell_ms))
  const seen = new Set<number>()

  return (
    <ol className="space-y-1">
      {views.map((view) => {
        const revisit = seen.has(view.slide_index)
        seen.add(view.slide_index)

        return (
          <li
            key={view.sequence}
            className="flex items-baseline gap-3 text-sm text-ink-body"
          >
            <span className="w-8 shrink-0 text-right tabular-nums text-ink-muted">
              {view.slide_index + 1}.
            </span>
            <span className="min-w-0 flex-1 truncate">
              {view.slide_title ?? `Slide ${view.slide_index + 1}`}
            </span>
            {revisit && (
              <span className="shrink-0 text-xs text-ink-muted">revisit</span>
            )}
            <span className="w-12 shrink-0 text-right tabular-nums">
              {formatClock(view.dwell_ms)}
            </span>
            <span className="w-16 shrink-0 text-xs text-accent">
              {/* Only mark the longest when there is something to compare it
                  to, and never on a zero — a single slide is not a winner. */}
              {views.length > 1 && view.dwell_ms === longest && longest > 0
                ? "longest"
                : ""}
            </span>
          </li>
        )
      })}
    </ol>
  )
}

function UploadVersionForm({ auditId, error }: { auditId: number; error?: string }) {
  const form = useForm<{ file: File | null }>({ file: null })

  const submit = (e: FormEvent) => {
    e.preventDefault()
    form.post(`/admin/audits/${auditId}/version`)
  }

  return (
    <form onSubmit={submit} className="space-y-2">
      <h2>Upload a new version</h2>
      <p className="text-sm text-ink-muted">
        The URL does not change. If the deck was deleted, uploading brings the
        link back.
      </p>
      <div className="space-y-2 pt-2">
        <label htmlFor="new_version_file">Deck file</label>
        <FileInput
          id="new_version_file"
          accept=".html,.htm,text/html"
          hint="The rebuilt .html deck"
          value={form.data.file}
          onFileChange={(file) => form.setData("file", file)}
          invalid={!!error}
          disabled={form.processing}
        />
        {error && <p className="text-xs text-danger-display">{error}</p>}
      </div>
      <Button type="submit" disabled={form.processing || !form.data.file}>
        {form.processing ? "Uploading…" : "Publish new version"}
      </Button>
    </form>
  )
}

function ExpiryForm({
  auditId,
  expiresOn,
  error,
}: {
  auditId: number
  expiresOn: string
  error?: string
}) {
  const form = useForm({ expires_at: expiresOn })

  const submit = (e: FormEvent) => {
    e.preventDefault()
    // The shared update action — it also backs the index's inline editing.
    form.patch(`/admin/audits/${auditId}`, { preserveScroll: true })
  }

  return (
    <form onSubmit={submit} className="space-y-2">
      <h2>Expiry</h2>
      <div className="space-y-2 pt-2">
        <label htmlFor="expires_at">Link works until the end of</label>
        <Input
          id="expires_at"
          type="date"
          aria-invalid={!!error}
          value={form.data.expires_at}
          onChange={(e) => form.setData("expires_at", e.target.value)}
          disabled={form.processing}
        />
        {error && <p className="text-xs text-danger-display">{error}</p>}
      </div>
      <Button type="submit" variant="secondary" disabled={form.processing}>
        Update expiry
      </Button>
    </form>
  )
}

function LifecycleActions({ audit }: { audit: AuditDetail }) {
  const [working, setWorking] = React.useState(false)

  const revoke = () => {
    setWorking(true)
    router.patch(`/admin/audits/${audit.id}/revoke`, {}, { onFinish: () => setWorking(false) })
  }

  const unrevoke = () => {
    setWorking(true)
    router.patch(`/admin/audits/${audit.id}/unrevoke`, {}, { onFinish: () => setWorking(false) })
  }

  const deleteDeck = () => {
    setWorking(true)
    router.delete(`/admin/audits/${audit.id}/deck`, { onFinish: () => setWorking(false) })
  }

  return (
    <div className="space-y-2">
      <h2>Lifecycle</h2>
      <p className="text-sm text-ink-muted">
        Both of these make the link show the dead-link page. Neither removes the
        record or its view history.
      </p>
      <div className="flex flex-wrap gap-2 pt-2">
        {audit.revoked_at ? (
          <Button variant="secondary" disabled={working} onClick={unrevoke}>
            Restore link
          </Button>
        ) : (
          <ConfirmAction
            trigger={
              <Button variant="secondary" disabled={working}>
                Revoke link
              </Button>
            }
            title="Revoke this link?"
            description={`Anyone opening ${audit.client_name}'s link will get the dead-link page from now on. The deck itself is kept, so you can still upload a new version later, and you can restore the link if you change your mind.`}
            confirmLabel="Revoke link"
            onConfirm={revoke}
          />
        )}

        {!audit.deck_deleted_at && (
          <ConfirmAction
            trigger={
              <Button variant="danger" disabled={working}>
                Delete deck
              </Button>
            }
            title="Delete this deck?"
            description={`The deck HTML for ${audit.client_name} is removed from every version and cannot be recovered — you would need to re-upload the built file. The audit record and its view history are kept, and the link starts showing the dead-link page.`}
            confirmLabel="Delete deck"
            onConfirm={deleteDeck}
          />
        )}
      </div>
    </div>
  )
}

function ConfirmAction({
  trigger,
  title,
  description,
  confirmLabel,
  onConfirm,
}: {
  trigger: React.ReactNode
  title: string
  description: string
  confirmLabel: string
  onConfirm: () => void
}) {
  return (
    <Dialog>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost">Cancel</Button>
          </DialogClose>
          <DialogClose asChild>
            <Button variant="danger" onClick={onConfirm}>
              {confirmLabel}
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
